import React, { useEffect, useState } from "react";
import { CheckCircle2, Loader2, Send, X } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

// Hook: public dropdown lists — no auth. Reads from `/api/public/returns/geo/*`
// which is backed by the same Mongo cache as the authenticated endpoint.
function usePublicCounties() {
  const [counties, setCounties] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    fetch(`${BACKEND}/api/public/returns/geo/counties`)
      .then((r) => r.ok ? r.json() : Promise.reject())
      .then((j) => { setCounties(j.items || []); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);
  return { counties, loading };
}

function usePublicCities(countyId) {
  const [cities, setCities] = useState([]);
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    if (!countyId) { setCities([]); return; }
    setLoading(true);
    fetch(`${BACKEND}/api/public/returns/geo/cities?county_id=${countyId}`)
      .then((r) => r.ok ? r.json() : Promise.reject())
      .then((j) => { setCities(j.items || []); setLoading(false); })
      .catch(() => setLoading(false));
  }, [countyId]);
  return { cities, loading };
}

const EMPTY = {
  customer_phone: "",
  order_id: "",
  customer_name: "",
  customer_email: "",
  pickup_county: "",
  pickup_city: "",
  pickup_address: "",
  bank_iban: "",
  product_name: "",
  reason: "",
  notes: "",
};

// Required fields (in display order). `notes` intentionally excluded — it's
// optional per product spec. Bank IBAN, reason are required.
const REQUIRED = [
  "customer_phone", "order_id", "customer_name", "customer_email",
  "pickup_county", "pickup_city", "pickup_address",
  "bank_iban", "product_name", "reason",
];

export default function PublicReturnForm() {
  const [cfg, setCfg] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [accept, setAccept] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null);
  const [error, setError] = useState(null);
  const [touched, setTouched] = useState(false); // any submit attempt
  const [lookupState, setLookupState] = useState({ loading: false, source: null }); // 'phone'|'order'
  const [confirmOpen, setConfirmOpen] = useState(false);

  useEffect(() => {
    fetch(`${BACKEND}/api/public/returns/form-config`)
      .then((r) => r.ok ? r.json() : Promise.reject())
      .then(setCfg)
      .catch(() => setCfg({
        title: "Formular de Retur",
        subtitle: "Completează Nr. Comandă sau Nr. de Telefon iar celelalte date din comandă vor fi completate automat. Ai grijă să completezi câmpurile rămase: Cont Bancar, Motiv Retur și Adresa Completă de Ridicare a Coletului.",
        privacy_policy_url: "",
        return_policy_url: "",
      }));
  }, []);

  const upd = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  // ── Auto-fill on blur of phone OR order_id ──────────────────────────
  // The customer types one identifier and the rest of the customer + order
  // fields populate from the backend. If both are typed, we prefer whichever
  // just got blurred (last-typed wins so the customer can correct after
  // an auto-fill).
  const lookup = async (source) => {
    const value = source === "phone" ? form.customer_phone.trim() : form.order_id.trim();
    if (!value) return;
    setLookupState({ loading: true, source });
    setError(null);
    try {
      const params = source === "phone" ? `phone=${encodeURIComponent(value)}` : `order_id=${encodeURIComponent(value)}`;
      const r = await fetch(`${BACKEND}/api/public/returns/lookup-order?${params}`);
      if (!r.ok) {
        const j = await r.json().catch(() => ({}));
        setError(j.detail || "Nu am găsit comanda. Verifică datele sau completează manual.");
        return;
      }
      const data = await r.json();
      // Merge — completăm automat DOAR datele de contact + produs, NU si
      // adresa de ridicare (user cerință 2026-02-04: clientul trebuie să
      // completeze el adresa completă de ridicare a coletului, chiar dacă
      // avem adresa de livrare din comandă — pot fi diferite).
      setForm((f) => ({
        ...f,
        customer_phone: f.customer_phone || data.customer_phone || "",
        order_id:       data.order_id || f.order_id,
        customer_name:  data.customer_name || f.customer_name,
        customer_email: data.customer_email || f.customer_email,
        product_name:   data.product_name || f.product_name,
      }));
    } catch (e) {
      setError(e.message);
    } finally {
      setLookupState({ loading: false, source: null });
    }
  };

  const isMissing = (k) => touched && REQUIRED.includes(k) && !String(form[k] || "").trim();
  const anyMissing = REQUIRED.some((k) => !String(form[k] || "").trim());

  // Submit chain: click "Trimite" → validate → open confirmation modal → send
  const openConfirm = (e) => {
    e.preventDefault();
    setTouched(true);
    setError(null);
    if (anyMissing) { setError("Te rugăm completează toate câmpurile marcate cu *."); return; }
    if (!accept)   { setError("Trebuie să accepți Politica de Confidențialitate și Politica de retur."); return; }
    setConfirmOpen(true);
  };

  const actuallySubmit = async () => {
    setSubmitting(true);
    try {
      const r = await fetch(`${BACKEND}/api/public/returns/create-return`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...form, policies_accepted: true }),
      });
      const j = await r.json();
      if (!r.ok) throw new Error(j.detail || "Eroare la trimitere");
      setDone(j);
      setConfirmOpen(false);
    } catch (err) {
      setError(err.message);
      setConfirmOpen(false);
    } finally {
      setSubmitting(false);
    }
  };

  if (!cfg) {
    return (
      <div className="min-h-screen bg-slate-950 text-slate-100 flex items-center justify-center">
        <Loader2 className="h-5 w-5 animate-spin text-slate-400" />
      </div>
    );
  }

  if (done) {
    return (
      <div className="min-h-screen bg-slate-950 text-slate-100 flex items-center justify-center px-4 py-10">
        <div data-testid="return-form-success" className="max-w-md w-full rounded-2xl border border-emerald-500/30 bg-emerald-500/5 p-8 text-center">
          <CheckCircle2 className="h-14 w-14 mx-auto text-emerald-400 mb-5" />
          <h1 className="text-2xl font-bold mb-4 text-emerald-100">Cerere trimisă cu succes!</h1>
          <p className="text-base text-slate-200 leading-relaxed">
            Cererea ta de retur a fost creată cu succes.
            <br />
            Te vom ține la curent pe email cu toate detaliile necesare.
          </p>
          <p className="mt-4 text-lg font-semibold text-emerald-300">Mulțumim!</p>
          {done.request_number && (
            <p className="mt-6 text-xs text-slate-400">
              Nr. cerere: <span className="font-mono text-emerald-300">#{done.request_number}</span>
            </p>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100 px-4 py-10">
      <div className="mx-auto max-w-2xl">
        <div className="rounded-2xl border border-slate-800 bg-slate-900/60 backdrop-blur p-6 sm:p-10 shadow-2xl">
          <h1 className="text-3xl sm:text-4xl font-extrabold mb-3 tracking-tight">{cfg.title}</h1>
          <p className="text-sm text-slate-300 mb-8 leading-relaxed">{cfg.subtitle}</p>

          <form onSubmit={openConfirm} className="space-y-4" data-testid="public-return-form" noValidate>
            {/* First two — used for the auto-fill lookup */}
            <Field label="Telefon" testid="rf-phone" type="tel"
              value={form.customer_phone} onChange={upd("customer_phone")}
              onBlur={() => lookup("phone")}
              hint={lookupState.loading && lookupState.source === "phone" ? "Se caută comanda…" : "La ieșirea din câmp, datele se completează automat"}
              missing={isMissing("customer_phone")} required />
            <Field label="Nr. comandă" testid="rf-order"
              value={form.order_id} onChange={upd("order_id")}
              onBlur={() => lookup("order")}
              hint={lookupState.loading && lookupState.source === "order" ? "Se caută comanda…" : "Sau completează aici — se auto-completează restul"}
              missing={isMissing("order_id")} required />

            <hr className="border-slate-800" />

            <Field label="Nume și Prenume" testid="rf-name" value={form.customer_name} onChange={upd("customer_name")}
              missing={isMissing("customer_name")} required />
            <Field label="Adresa de email" testid="rf-email" type="email" value={form.customer_email} onChange={upd("customer_email")}
              missing={isMissing("customer_email")} required />
            <CountyCityDropdowns
              county={form.pickup_county}
              city={form.pickup_city}
              onChange={({ county, city }) => setForm((f) => ({ ...f, pickup_county: county, pickup_city: city }))}
              countyMissing={isMissing("pickup_county")}
              cityMissing={isMissing("pickup_city")}
              testidCounty="rf-county"
              testidCity="rf-city"
            />
            <Field label="Adresă ridicare colet" testid="rf-address" value={form.pickup_address} onChange={upd("pickup_address")}
              missing={isMissing("pickup_address")} required />
            <Field label="Cont bancar pentru returnare contravaloare" testid="rf-iban" value={form.bank_iban} onChange={upd("bank_iban")}
              placeholder="RO49AAAA1B31007593840000"
              missing={isMissing("bank_iban")} required />
            <Field label="Nume produs returnat" testid="rf-product" value={form.product_name} onChange={upd("product_name")}
              missing={isMissing("product_name")} required />
            <Field label="Motivul returnării produsului" testid="rf-reason" textarea value={form.reason} onChange={upd("reason")}
              missing={isMissing("reason")} required />
            <Field label="Observații" testid="rf-notes" textarea value={form.notes} onChange={upd("notes")} />

            <label className="flex items-start gap-3 pt-3 cursor-pointer">
              <input data-testid="rf-accept" type="checkbox" checked={accept}
                onChange={(e) => setAccept(e.target.checked)}
                className="mt-0.5 h-4 w-4 rounded border-slate-700 bg-slate-800 text-emerald-500 focus:ring-emerald-500" />
              <span className="text-xs text-slate-300 leading-relaxed">
                Sunt de acord cu prelucrarea datelor cu caracter personal conform{" "}
                {cfg.privacy_policy_url ? (
                  <a href={cfg.privacy_policy_url} target="_blank" rel="noreferrer" className="text-emerald-300 underline hover:text-emerald-200">Politicii de Confidențialitate</a>
                ) : (<span className="text-emerald-300 underline">Politicii de Confidențialitate</span>)}
                {" "}și am citit{" "}
                {cfg.return_policy_url ? (
                  <a href={cfg.return_policy_url} target="_blank" rel="noreferrer" className="text-emerald-300 underline hover:text-emerald-200">Politica de retur</a>
                ) : (<span className="text-emerald-300 underline">Politica de retur</span>)}
                .<span className="text-rose-400"> *</span>
              </span>
            </label>
            {touched && !accept && (
              <div className="pl-7 text-xs text-rose-400">*Necompletat — trebuie să bifezi acordul ca să poți trimite cererea</div>
            )}

            {/* Notice — request from product spec */}
            <div className="rounded-md border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-xs text-amber-100">
              Citește cu atenție toate datele din această cerere chiar dacă au fost completate automat.
            </div>

            {error && (
              <div data-testid="rf-error" className="rounded-md border border-rose-500/40 bg-rose-500/10 px-3 py-2 text-sm text-rose-200">
                {error}
              </div>
            )}

            <button data-testid="rf-submit" type="submit"
              className="w-full inline-flex items-center justify-center gap-2 rounded-lg bg-emerald-500 px-4 py-3 text-sm font-semibold text-white hover:bg-emerald-400 transition">
              <Send className="h-4 w-4" />
              Trimite cererea de retur
            </button>
          </form>
        </div>
      </div>

      {/* Confirmation modal */}
      {confirmOpen && (
        <div data-testid="rf-confirm-modal" className="fixed inset-0 z-[100] flex items-center justify-center bg-black/75 px-4">
          <div className="w-full max-w-md rounded-2xl border border-slate-700 bg-slate-900 p-6 shadow-2xl">
            <div className="mb-4 flex items-start justify-between gap-3">
              <h2 className="text-lg font-bold text-slate-100">Confirmă trimiterea</h2>
              <button data-testid="rf-confirm-x" onClick={() => setConfirmOpen(false)} className="rounded-md p-1 text-slate-400 hover:bg-slate-800 hover:text-slate-100">
                <X className="h-4 w-4" />
              </button>
            </div>
            <p className="text-sm text-slate-200 leading-relaxed">
              Ești sigur că vrei să trimiți această cerere de retur către bragus.ro?
            </p>
            <div className="mt-6 flex gap-3">
              <button data-testid="rf-confirm-no" onClick={() => setConfirmOpen(false)} disabled={submitting}
                className="flex-1 rounded-lg border border-slate-600 bg-slate-800 px-4 py-2.5 text-sm font-semibold text-slate-200 hover:bg-slate-700 disabled:opacity-50">
                NU — mai vreau să verific
              </button>
              <button data-testid="rf-confirm-yes" onClick={actuallySubmit} disabled={submitting}
                className="flex-1 inline-flex items-center justify-center gap-2 rounded-lg bg-emerald-500 px-4 py-2.5 text-sm font-semibold text-white hover:bg-emerald-400 disabled:opacity-50">
                {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                {submitting ? "Se trimite..." : "DA — trimite"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Field({ label, testid, value, onChange, onBlur, type = "text", textarea, placeholder, hint, missing, required }) {
  return (
    <div>
      <label className="block text-xs font-medium text-slate-200 mb-1.5">
        {label}{required && <span className="text-rose-400 ml-1">*</span>}
      </label>
      {textarea ? (
        <textarea data-testid={testid} value={value} onChange={onChange} onBlur={onBlur} placeholder={placeholder} rows={3}
          className={`w-full rounded-md border bg-slate-900/80 px-3 py-2 text-sm text-slate-100 outline-none focus:border-emerald-500/60 ${missing ? "border-rose-500/60" : "border-slate-700"}`} />
      ) : (
        <input data-testid={testid} type={type} value={value} onChange={onChange} onBlur={onBlur} placeholder={placeholder}
          className={`w-full rounded-md border bg-slate-900/80 px-3 py-2 text-sm text-slate-100 outline-none focus:border-emerald-500/60 ${missing ? "border-rose-500/60" : "border-slate-700"}`} />
      )}
      {missing && (
        <div data-testid={`${testid}-missing`} className="mt-1 text-[11px] text-rose-400">
          *Necompletat — trebuie să completezi acest câmp ca să poți trimite cererea
        </div>
      )}
      {!missing && hint && (
        <div className="mt-1 text-[11px] text-slate-500">{hint}</div>
      )}
    </div>
  );
}

// Dropdown-uri pentru județ + oraș (Sameday nomenclator), folosite in
// formularele publice de retur/garanție. Clientul alege din listă in loc
// să scrie manual → validare la Sameday zero probleme.
function CountyCityDropdowns({ county, city, onChange, countyMissing, cityMissing, testidCounty, testidCity }) {
  const { counties, loading: cLoading } = usePublicCounties();
  const selected = counties.find((c) => c.name === county) || null;
  const { cities, loading: ciLoading } = usePublicCities(selected?.id);
  return (
    <>
      <div>
        <label className="block text-xs font-medium text-slate-200 mb-1.5">
          Județ ridicare colet<span className="text-rose-400 ml-1">*</span>
        </label>
        <select
          data-testid={testidCounty}
          value={county || ""}
          onChange={(e) => onChange({ county: e.target.value, city: "" })}
          className={`w-full rounded-md border bg-slate-900/80 px-3 py-2 text-sm text-slate-100 outline-none focus:border-emerald-500/60 ${countyMissing ? "border-rose-500/60" : "border-slate-700"}`}
        >
          <option value="">{cLoading ? "Se încarcă județele..." : "— alege județul —"}</option>
          {counties.map((c) => (
            <option key={c.id} value={c.name}>{c.name}</option>
          ))}
        </select>
        {countyMissing && (
          <div data-testid={`${testidCounty}-missing`} className="mt-1 text-[11px] text-rose-400">
            *Necompletat — alege județul din listă
          </div>
        )}
      </div>
      <div>
        <label className="block text-xs font-medium text-slate-200 mb-1.5">
          Oraș ridicare colet<span className="text-rose-400 ml-1">*</span>
        </label>
        <select
          data-testid={testidCity}
          value={city || ""}
          onChange={(e) => onChange({ county, city: e.target.value })}
          disabled={!selected}
          className={`w-full rounded-md border bg-slate-900/80 px-3 py-2 text-sm text-slate-100 outline-none focus:border-emerald-500/60 disabled:opacity-50 ${cityMissing ? "border-rose-500/60" : "border-slate-700"}`}
        >
          <option value="">{!selected ? "alege întâi județul" : ciLoading ? "Se încarcă orașele..." : "— alege orașul —"}</option>
          {cities.map((c) => (
            <option key={c.id} value={c.name}>{c.name}</option>
          ))}
        </select>
        {cityMissing && (
          <div data-testid={`${testidCity}-missing`} className="mt-1 text-[11px] text-rose-400">
            *Necompletat — alege orașul din listă
          </div>
        )}
      </div>
    </>
  );
}
